import React, { useState } from "react";
import { Upload, X, Send, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function SellCarPage() {
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    brand: "",
    model: "",
    year: "",
    kilometers: "",
    expected_price: "",
    message: "",
  });
  const [photoUrls, setPhotoUrls] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => { document.title = "Sell Your Car — Faisal Car Bazar"; }, []);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPreviews((prev) => [...prev, ev.target.result]);
        setPhotoUrls((prev) => [...prev, ev.target.result]); // store base64 client-side for now
      };
      reader.readAsDataURL(f);
    });
  };

  const removePhoto = (i) => {
    setPreviews((prev) => prev.filter((_, idx) => idx !== i));
    setPhotoUrls((prev) => prev.filter((_, idx) => idx !== i));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/sell-submissions", {
        name: form.name,
        mobile: form.mobile,
        brand: form.brand,
        model: form.model,
        year: Number(form.year),
        kilometers: Number(form.kilometers),
        expected_price: Number(form.expected_price),
        message: form.message,
        images: photoUrls,
      });
      setDone(true);
      toast.success("Submitted! We'll contact you with an offer.");
    } catch {
      toast.error("Submission failed, please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="sell-success" className="max-w-2xl mx-auto px-6 py-32 text-center">
        <CheckCircle2 className="mx-auto text-[#D4AF37] mb-6" size={56} />
        <h1 className="font-display text-4xl text-[#F5F5DC] mb-4">Submission Received</h1>
        <p className="text-[#A0A0A0] mb-8">
          Thank you, {form.name}. Our team will call you on {form.mobile} within 24 hours with a competitive offer.
        </p>
        <button onClick={() => { setDone(false); setForm({ name: "", mobile: "", brand: "", model: "", year: "", kilometers: "", expected_price: "", message: "" }); setPhotoUrls([]); setPreviews([]); }} className="btn-secondary">Submit Another</button>
      </div>
    );
  }

  return (
    <div data-testid="sell-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-12">
        <div className="eyebrow mb-4">Sell Your Car</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F5DC] font-light">
          Best price. <span className="text-[#DC143C] italic">Zero hassle.</span>
        </h1>
        <div className="luxe-divider" />
        <p className="text-[#A0A0A0] mt-4 max-w-xl leading-relaxed">
          Fill in your car details and upload photos. Our valuation team will call you with a competitive offer within 24 hours.
        </p>
      </div>

      <form onSubmit={submit} className="premium-card p-8" data-testid="sell-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Your Name" testId="sell-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <Field label="Mobile Number" testId="sell-mobile" type="tel" value={form.mobile} onChange={(v) => setForm({ ...form, mobile: v })} required />
          <Field label="Car Brand" testId="sell-brand" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} required placeholder="e.g. Maruti Suzuki" />
          <Field label="Car Model" testId="sell-model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} required placeholder="e.g. Swift ZXi+" />
          <Field label="Year" testId="sell-year" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} required />
          <Field label="Kilometers Driven" testId="sell-km" type="number" value={form.kilometers} onChange={(v) => setForm({ ...form, kilometers: v })} required />
          <Field label="Expected Price (₹)" testId="sell-price" type="number" value={form.expected_price} onChange={(v) => setForm({ ...form, expected_price: v })} required />
        </div>

        <div className="mt-5">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Message (optional)</label>
          <textarea
            data-testid="sell-message"
            rows={3}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-[#F5F5DC] focus:border-[#DC143C] outline-none resize-none"
          />
        </div>

        <div className="mt-5">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Upload Car Photos (max 8)</label>
          <label className="block border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-[#DC143C] transition-colors" data-testid="sell-upload-label">
            <Upload className="mx-auto text-[#D4AF37] mb-3" size={28} />
            <span className="text-sm text-[#F5F5DC]/80">Click to upload images</span>
            <input
              data-testid="sell-upload-input"
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={onFiles}
              disabled={previews.length >= 8}
            />
          </label>
          {previews.length > 0 && (
            <div className="grid grid-cols-4 gap-3 mt-4">
              {previews.map((src, i) => (
                <div key={src.slice(0, 60) + i} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                  <img src={src} alt={`upload ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-[#DC143C]"
                    data-testid={`sell-remove-photo-${i}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="sell-submit"
          className="btn-primary w-full justify-center mt-8"
        >
          {submitting ? "Submitting…" : <>Get My Quote <Send size={16} /></>}
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-[#F5F5DC] focus:border-[#DC143C] outline-none placeholder:text-white/30"
      />
    </div>
  );
}
